import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnInit } from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { StandardSchedule, StandardScheduleService } from "../../services/standard.schedule.service";
import { DropDownItem } from "../../pojo/DropDownItem";
import { EventFormOccurrenceUtil } from "../s25-event-creation-form/occurrences/event.form.occurrence.util";
import { FlsService } from "../../services/fls.service";
import { TelemetryService } from "../../services/telemetry.service";
import { UserprefService } from "../../services/userpref.service";
import { S25Util } from "../../util/s25-util";

export interface DashMeetingPatternApiBean {
    getOccurrences?: () => import("../../pojo/Event").Event.Occurrence[];
    getStartEndDates?: () => { startDt: Date; endDt: Date }[];
    getProfileCode?: () => string | null;
    reset?: () => void;
    isValid?: () => boolean;
}

@TypeManagerDecorator("s25-ng-dash-meeting-pattern-picker")
@Component({
    selector: "s25-ng-dash-meeting-pattern-picker",
    template: `
        @if (isInit) {
            <div class="dash-meeting-pattern-picker">
                @if (!standardSchedules.length && canAccessStandardSchedules) {
                    <p>No standard schedules are defined in your environment.</p>
                    @if (canAccessStandardSchedules) {
                        <a
                            class="text-main"
                            s25-ng-router-link
                            [stateName]="'standardSchedules'"
                            (click)="trackGoToStandardSchedules()"
                        >
                            Go to Standard Schedules
                        </a>
                    }
                } @else {
                    <div class="dash-meeting-pattern-picker__field">
                        <label class="ngBold">Standard Schedule</label>
                        <s25-generic-dropdown
                            ariaLabel="Standard Schedule"
                            [items]="standardSchedules"
                            placeholder="Select a Standard Schedule"
                            [searchEnabled]="true"
                            [(value)]="chosenSchedule"
                            (valueChange)="onScheduleChange($event)"
                        />
                    </div>
                    @if (chosenSchedule) {
                        <div class="dash-meeting-pattern-picker__field">
                            <label class="ngBold">Meeting Pattern</label>
                            <s25-generic-dropdown
                                ariaLabel="Meeting Pattern"
                                [items]="meetingPatterns"
                                placeholder="Select a Meeting Pattern"
                                [searchEnabled]="true"
                                [(value)]="chosenPattern"
                                (valueChange)="onPatternChange($event)"
                            />
                        </div>
                    }
                    <div class="dash-meeting-pattern-picker__dates">
                        <s25-datepicker
                            [(value)]="termStartDate"
                            inputLabel="Start Date"
                            inputId="dash-find-location-term-start"
                            [allowEmpty]="true"
                            [autoSelectToday]="false"
                            [popoverOnBody]="true"
                        />
                        <s25-datepicker
                            [(value)]="termEndDate"
                            inputLabel="End Date"
                            inputId="dash-find-location-term-end"
                            [allowEmpty]="true"
                            [autoSelectToday]="false"
                            [popoverOnBody]="true"
                        />
                    </div>
                }
            </div>
        }
    `,
    styles: `
        .dash-meeting-pattern-picker__field {
            margin-bottom: 0.75em;
        }

        .dash-meeting-pattern-picker__dates {
            display: flex;
            flex-wrap: wrap;
            gap: 1em;
        }

        .dash-meeting-pattern-picker__dates s25-datepicker {
            flex: 1 1 180px;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class S25DashMeetingPatternPickerComponent implements OnInit {
    @Input() apiBean: DashMeetingPatternApiBean = {};

    isInit = false;
    standardSchedules: DropDownItem[] = [];
    meetingPatterns: DropDownItem[] = [];
    chosenSchedule: DropDownItem | null = null;
    chosenPattern: DropDownItem | null = null;
    selectedPattern: StandardSchedule | null = null;
    private patternById = new Map<number, StandardSchedule>();
    termStartDate: Date | null = null;
    termEndDate: Date | null = null;
    canAccessStandardSchedules = false;
    timeFormat: string;

    constructor(
        private elementRef: ElementRef,
        private changeDetectorRef: ChangeDetectorRef,
    ) {
        this.elementRef.nativeElement.angBridge = this;
    }

    async ngOnInit() {
        this.timeFormat = await UserprefService.getS25Timeformat();
        const [schedules, fls, groupId] = await Promise.all([
            StandardScheduleService.getStandardScheduleList(4),
            FlsService.getFls(),
            UserprefService.getGroupId(),
        ]);
        this.canAccessStandardSchedules = fls.M25_EXPORT !== "N" || groupId === -1;
        this.standardSchedules = S25Util.array.forceArray(schedules).map((schedule) => ({
            itemId: schedule.id,
            itemName: schedule.name,
        }));
        this.bindApiBean();
        this.isInit = true;
        this.changeDetectorRef.detectChanges();
    }

    bindApiBean() {
        const self = this;
        Object.assign(this.apiBean, {
            getOccurrences: () => self.getBuiltOccurrences().occurrences,
            getStartEndDates: () =>
                self.getBuiltOccurrences().occurrences.map((occurrence) => ({
                    startDt: EventFormOccurrenceUtil.getStartDt(occurrence),
                    endDt: EventFormOccurrenceUtil.getEndDt(occurrence),
                })),
            getProfileCode: () => self.getBuiltOccurrences().profileCode,
            reset: () => self.reset(),
            isValid: () => self.isValid(),
        });
    }

    getBuiltOccurrences() {
        if (!this.selectedPattern || !this.termStartDate || !this.termEndDate) {
            return { occurrences: [], profileCode: null as string | null };
        }
        if (this.termEndDate < this.termStartDate) {
            return { occurrences: [], profileCode: null as string | null };
        }
        return StandardScheduleService.buildOccurrencesForTerm(
            this.selectedPattern,
            this.termStartDate,
            this.termEndDate,
        );
    }

    isValid() {
        const built = this.getBuiltOccurrences();
        return !!this.selectedPattern && !!this.termStartDate && !!this.termEndDate && built.occurrences.length > 0;
    }

    async onScheduleChange(schedule: DropDownItem | null) {
        this.chosenSchedule = schedule;
        this.chosenPattern = null;
        this.selectedPattern = null;
        this.meetingPatterns = [];
        if (schedule?.itemId) {
            TelemetryService.sendWithSub("DashFindLocation", "meetingPattern", "SelectSchedule");
            const patterns = await StandardScheduleService.getStandardSchedule(Number(schedule.itemId));
            this.patternById.clear();
            this.meetingPatterns = S25Util.array.forceArray(patterns).map((pattern) => {
                this.patternById.set(pattern.id!, pattern);
                return {
                    itemId: pattern.id,
                    itemName: StandardScheduleService.formatPatternLabel(pattern, this.timeFormat),
                };
            });
        }
        this.changeDetectorRef.detectChanges();
    }

    onPatternChange(patternItem: DropDownItem | null) {
        this.chosenPattern = patternItem;
        this.selectedPattern =
            patternItem?.itemId != null ? (this.patternById.get(Number(patternItem.itemId)) ?? null) : null;
        if (patternItem) {
            TelemetryService.sendWithSub("DashFindLocation", "meetingPattern", "SelectPattern");
        }
        this.changeDetectorRef.detectChanges();
    }

    reset() {
        this.chosenSchedule = null;
        this.chosenPattern = null;
        this.selectedPattern = null;
        this.meetingPatterns = [];
        this.patternById.clear();
        this.termStartDate = null;
        this.termEndDate = null;
        this.changeDetectorRef.detectChanges();
    }

    trackGoToStandardSchedules() {
        TelemetryService.sendWithSub("DashFindLocation", "meetingPattern", "GoToStandardSchedules");
    }
}
